import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  AUTO_CANCELLED,
  formatDate,
  sentenceCase,
} from "../../../utility-functions";
import AnimationPage from "../../shared/animation-page/AnimationPage";
import Button, { ViewButton } from "../../design/Button";
import GModal from "../../design/components/GModal";
import GTable from "../../design/components/GTable";
import Tag from "../../whatsapp-business/Components/Tag";

import AddOAuthAppModel from "./AddOAuthAppModal";
import CopyApiKeyModal from "./CopyApiKeyModal";
import CreateEditApiKeyModal from "./CreateEditApiKeyModal";
import DeleteAppModal from "./DeleteAppModal";
import RevokeDeprecateApiKeyConfirmationModal from "./RevokeDeprecateApiKeyConfirmationModal";

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import { Divider } from "rsuite";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import threedotsIcon from "../../../assets/gcall-logs-threedots-icon.svg";

import { JService } from "../../api-service/ApiService";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import "./Developer.css";
import styles from "../../design/styles";

const BASE_URL = process.env.REACT_APP_BETA;
const APP_URL = `${BASE_URL}/api/v2/oauth/apps/`;
const API_KEY_URL = `${BASE_URL}/api/v2/integrations/api-keys/`;

function DeveloperTab(props) {
  const navigate = useNavigate();  

  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [selectedAppName, setSelectedAppName] = useState("");
  const [countApps, setCountApps] = useState(0);
  const [appModal, setAppModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formValues, setFormValues] = useState({
    appName: "",
    redirectUrl: "",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAppId, setMenuAppId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [selectedAppExternal, setSelectedAppExternal] = useState(false);

  const [apiKeys, setApiKeys] = useState([]);
  const [totalApiKeys, setTotalApiKeys] = useState(0);
  const [apiKeysPageData, setApiKeysPageData] = useState({
    currentPage: 0,
    nextUrl: null,
    previousUrl: null,
  });
  const [gTablePageNumber, setGTablePageNumber] = useState(0);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [updatingApiKey, setUpdatingApiKey] = useState(false);
  const [apiKeyModal, setApiKeyModal] = useState({ visible: false, mode: "" });
  const [copyApiKeyModal, setCopyApiKeyModal] = useState({
    visible: false,
    apiKey: "",
  });
  const [currentRow, setCurrentRow] = useState(null);
  const [rowOptionsVisible, setRowOptionsVisible] = useState(false);
  const [rowOptionsCords, setRowOptionsCords] = useState([0, 0]);

  const apiKeyFetchUrlRef = useRef(null);

  const handleMenuOpen = (event, appId) => {
    setAnchorEl(event.currentTarget);
    setMenuAppId(appId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuAppId(null);
  };

  const handleDeleteButton = (id, name, external) => {
    console.log("Delete app", id);
    setSelectedAppId(id);
    setSelectedAppName(name);
    setSelectedAppExternal(external);
    handleMenuClose();
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    let action = selectedAppExternal ? "uninstalled" : "deleted";
    let action_error = selectedAppExternal ? "uninstalling" : "deleting";
    JService.delete(`${APP_URL}${selectedAppId}/`)
      .then((response) => {
        console.log(`App: ${selectedAppId} - ${action}`);
        enqueueSnackbar(`App ${action} successfully`, { variant: "success" });

        setApps((prevApps) =>
          prevApps.filter((app) => app.id !== selectedAppId),
        );

        setShowDeleteModal(false);
      })
      .catch((error) => {
        console.error("Error:", error);
        enqueueSnackbar(
          error?.message ?? `Error while ${action_error} the app`,
          { variant: "error" },
        );
      });
  };
  // CREATE APP
  const handleOpen = () => setAppModal(true);
  const handleClose = () => {
    setAppModal(false);
    navigate("/settings?tab=Developer");
  };
  const handleSave = () => {
    if (isSubmitted) return;
    setIsSubmitted(true);

    const payload = {
      name: formValues.appName,
      redirect_uris: formValues.redirectUrl,
      client_type: "public",
    };
    JService.post(APP_URL, payload)
      .then((response) => {
        console.log("Raw response from JService.post:", response);
        const appId = response.data?.app_id;
        enqueueSnackbar("App created successfully.", { variant: "success" });
        if (appId) {
          navigate(`/settings/apps/${appId}`);
          setAppModal(false);
        } else {
          console.warn("No appId in response; navigating to base /settings");
          setAppModal(false);
          navigate(`/settings`);
        }
      })
      .catch((error) => {
        console.error("Failed to create app:", error);
        enqueueSnackbar("Failed to created app", { variant: "error" });
        navigate(`/settings`);
        setAppModal(false);
      });
  };

  const fetchApiKeys = async (fetchType = "initial", pageSize = 5) => {
    setApiKeysLoading(true);
    try {
      let url = API_KEY_URL + `?page=1&page_size=${pageSize}`;
      if (fetchType === "next") {
        url = apiKeysPageData?.nextUrl;
      } else if (fetchType === "previous") {
        url = apiKeysPageData?.previousUrl;
      } else if (fetchType === "refresh") {
        url = apiKeyFetchUrlRef.current;
      }
      const response = await JService.get(url);
      if (!response.success) {
        throw new Error(response?.message ?? "Failed to fetch API keys");
      }
      apiKeyFetchUrlRef.current = url;
      setApiKeys(response?.data?.results ?? []);
      setTotalApiKeys(response?.data?.count ?? 0);
      setApiKeysPageData({
        currentPage: parseInt(new URLSearchParams(url).get("page")) || 1,
        nextUrl: response?.data?.next,
        previousUrl: response?.data?.previous,
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error?.message ?? "Failed to fetch API keys", {
        variant: "error",
      });
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleCreateApiKey = async (apiKeyName) => {
    setUpdatingApiKey(true);
    try {
      const response = await JService.post(
        "/api/v2/integrations/obtain-api-key/",
        {
          name: apiKeyName,
        },
      );
      if (!response.success) {
        throw new Error(response?.message ?? "Failed to create API Key");
      }
      enqueueSnackbar("API Key created successfully.", {
        variant: "success",
      });
      setCopyApiKeyModal({
        visible: true,
        apiKey: response?.["api-key"],
      });
      await fetchApiKeys("refresh");
    } catch (error) {
      console.error("Failed to create API Key:", error);
      enqueueSnackbar(error?.message ?? "Failed to create API Key", {
        variant: "error",
      });
    } finally {
      setUpdatingApiKey(false);
      setApiKeyModal({ visible: false, mode: "" });
    }
  };

  const handleUpdateApiKey = async (apiKeyName, mode) => {
    const _mode = mode ?? apiKeyModal.mode;
    const action = _mode === "edit" ? "updated" : String(_mode) + "d";
    try {
      setUpdatingApiKey(true);
      if (_mode === "reactivate") {
        setApiKeysLoading(true);
      }
      const payload = {
        prefix: currentRow.prefix,
      };
      if (_mode === "edit") {
        payload.name = apiKeyName;
      } else if (["deprecate", "revoke"].includes(_mode)) {
        payload.status = _mode + "d";
      } else if (_mode === "reactivate") {
        payload.status = "active";
      }
      const response = await JService.patch(
        API_KEY_URL + currentRow?.id + "/",
        payload,
      );
      if (!response.success) {
        throw new Error(response?.message ?? "Failed to " + _mode + " API Key");
      }
      await fetchApiKeys();
      enqueueSnackbar("API Key " + action + " successfully.", {
        variant: "success",
      });
    } catch (error) {
      console.error("Failed to " + _mode + " API Key:", error);
      enqueueSnackbar(error?.message ?? "Failed to " + _mode + " API Key", {
        variant: "error",
      });
      if (apiKeysLoading) {
        setApiKeysLoading(false);
      }
    } finally {
      setApiKeyModal({ visible: false, mode: "" });
      setUpdatingApiKey(false);
    }
  };

  useEffect(() => {
    JService.get(APP_URL)
      .then((response) => {
        setCountApps(response.count);
        setApps(response.apps);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const apiKeyColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        headerClassName: "api-keys-table-header",
        flex: 0.24,
        renderCell: (params) => {
          return (
            <span
              className="t7 medium-font nc-gray-900 no-overflow api-keys-name-cell"
              title={params.row.name}
            >
              {params.row.name}
            </span>
          );
        },
      },
      {
        field: "key",
        headerName: "Key",
        headerClassName: "api-keys-table-header",
        flex: 0.24,
        renderCell: (params) => {
          return <span>{params.row.prefix + "•••••••••••••••"}</span>;
        },
      },
      {
        field: "created_at",
        headerName: "Created",
        headerClassName: "api-keys-table-header",
        flex: 0.176,
        renderCell: (params) => {
          return <span>{formatDate(params.row.created_at)}</span>;
        },
      },
      {
        field: "status",
        headerName: "Status",
        headerClassName: "api-keys-table-header",
        flex: 0.25,
        renderCell: (params) => {
          const status = params.row.status;
          const color =
            status === "active"
              ? "GREEN"
              : status === "deprecated"
                ? "YELLOW"
                : status === "revoked"
                  ? "RED"
                  : "UNKNOWN";
          return (
            <div className="api-keys-status-cell">
              <Tag quality={color} name={sentenceCase(status)} />
              {status === "deprecated" && (
                <p className="t7 api-keys-status-cell-text">
                  Expires {params.row.deprecation_remaining_days == 0 ? 'today' : `in ${params.row.deprecation_remaining_days} days`}
                </p>
              )}
            </div>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        headerClassName: "api-keys-table-header",
        flex: 0.08,
        cellClassName: "table-cell-edit",
        renderCell: (params) => {
          return (
            <Box sx={{ textAlign: "left" }}>
              <img
                onClick={(e) => {
                  e.stopPropagation();
                  if (params?.row?.status === "revoked") return;
                  setRowOptionsCords([e["clientX"], e["clientY"]]);
                  setCurrentRow(params.row);
                  setRowOptionsVisible(true);
                }}
                src={threedotsIcon}
                alt="three dots icon"
              />
            </Box>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      {loading ? (
        <AnimationPage variant="transparent" height="200px" />
      ) : countApps === 0 ? (
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems="left"
          gap="20px"
          justifyContent="space-between"
          p={1}
          sx={{ backgroundColor: "white" }}
        >
          <Box sx={{ width: "fit-content", height: 64 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "28px",
                letterSpacing: "0%",
                marginBottom: "18px",
              }}
            >
              OAuth apps
            </Typography>
            <Typography
              variant="body3"
              sx={{
                lineHeight: "20px",
                fontSize: "14px",
              }}
            >
              Create apps to integrate with FreJun using OAuth 2.0.
            </Typography>
          </Box>
          <button
            className="create-app-btn"
            onClick={handleOpen}
            disabled={props.status === AUTO_CANCELLED}
          >
            Create app
          </button>
        </Box>
      ) : (
        <Box gap={3} marginBottom={4.5}>
          <Box
            className="create-app"
            display="flex"
            flexDirection="row"
            alignItems="left"
            gap={2}
            justifyContent="space-between"
            p={1}
            sx={{ backgroundColor: "white" }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 500,
                fontSize: "18px",
                lineHeight: "28px",
                letterSpacing: "0%",
                marginBottom: "18px",
              }}
            >
              OAuth apps
            </Typography>
            <button
              className="create-app-btn"
              onClick={handleOpen}
              disabled={props.status === AUTO_CANCELLED}
            >
              Create app
            </button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: "#f7f9f8" }}>
                <TableRow>
                  <TableCell sx={styles.t7}>App name</TableCell>
                  <TableCell sx={styles.t7} align="left">
                    Type
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {apps.map((app, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: "#ffffff",
                      "&:hover": {
                        backgroundColor: "#FAFAFA",
                        "& .menu-button": {
                          visibility: "visible",
                        },
                        "& .view-button": {
                          visibility: "visible !important",
                        },
                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          noWrap
                          sx={{ ...styles.t7, maxWidth: "300px" }}
                        >
                          {app.name}
                        </Typography>
                        {!app.external && (
                          <ViewButton
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/settings/apps/${app.id}`);
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={styles.t7}>
                        {app.external ? "External" : "Internal"}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            handleMenuOpen(e, app.id);
                          }}
                          sx={{
                            visibility: "hidden",
                            backgroundColor: "#fff",
                            border: "1px solid #E4E4E7",
                            borderRadius: "6px",
                            padding: "8px",
                            transform: "rotate(90deg)",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          }}
                          className="menu-button"
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>

                    {menuAppId === app.id && (
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <MenuItem
                          onClick={(e) => {
                            handleDeleteButton(app.id, app.name, app.external);
                          }}
                        >
                          {app.external ? "Uninstall" : "Delete"}
                        </MenuItem>
                      </Menu>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <GModal
        visible={appModal}
        closeModal={handleClose}
        heading="Create App"
        body={
          <AddOAuthAppModel
            onFormChange={(valid, values) => {
              setIsFormValid(valid);
              setFormValues(values);
            }}
          />
        }
        modalStyle={{
          height: "auto",
          width: "30rem",
          padding: "32px 24px 24px 24px",
          my: "2rem",
        }}
        bottomButtons={[
          <button
            className="cancel-btn"
            onClick={handleClose}
            disabled={isSubmitted}
          >
            Cancel
          </button>,
          <button
            className="create-btn"
            onClick={handleSave}
            disabled={!isFormValid || isSubmitted}
          >
            {isSubmitted ? "Creating" : "Create"}
          </button>,
        ]}
        footerLeft={
          <a href="https://frejun.com/docs/" className="gmodal-footer-link">
            View docs
          </a>
        }
      />
      <DeleteAppModal
        visible={showDeleteModal}
        closeModal={() => setShowDeleteModal(false)}
        onDelete={() => {
          console.log("Deleting app:", selectedAppId);
          handleDelete();
          setShowDeleteModal(false);
        }}
        appName={selectedAppName}
        external={selectedAppExternal}
      />

      <Divider />
      <div className="api-key-container">
        <div
          className={`section api-section ${totalApiKeys > 0 || apiKeysLoading ? "flex-row" : ""}`}
        >
          <h1 className="t6 medium-font nc-gray-900">API keys</h1>
          {!apiKeysLoading && totalApiKeys <= 0 && (
            <p className="t7 regular-font nc-gray-900">
              Create an API key to connect your integrations and start making
              API calls.
            </p>
          )}
          <Button
            variant="contained"
            buttonText="Create key"
            hierarchy="green"
            onClick={() => setApiKeyModal({ visible: true, mode: "create" })}
            buttonStyle={{
              height: "40px",
              width: "120px",
              fontSize: "14px",
              padding: "6px",
            }}
            disabled={props.status === AUTO_CANCELLED}
          />
        </div>
        <GTable
          hideHeader={true}
          rows={apiKeys}
          columns={apiKeyColumns}
          useMuiTable={true}
          isLoading={apiKeysLoading}
          onRowClick={() => null}
          rowCount={totalApiKeys}
          rowsPerPage={5}
          handlePageChange={(_, direction) => fetchApiKeys(direction)}
          onPageSizeChange={(pageSize) => {
            setGTablePageNumber(0);
            fetchApiKeys("initial", pageSize);
          }}
          paginationMode="server"
          fromScreen="api-keys"
          pageSizeOptions={[5, 10, 25]}
          gTablePageNumber={gTablePageNumber}
          setGTablePageNumber={setGTablePageNumber}
          styleOverride={{
            display: totalApiKeys <= 0 && !apiKeysLoading ? "none" : "",
          }}
          getRowId={(row) => row.prefix}
        />
      </div>
      <CreateEditApiKeyModal
        mode={apiKeyModal.mode}
        visible={
          apiKeyModal.visible && ["create", "edit"].includes(apiKeyModal.mode)
        }
        onClose={() => setApiKeyModal({ visible: false, mode: "" })}
        onSave={
          apiKeyModal.mode === "create"
            ? handleCreateApiKey
            : handleUpdateApiKey
        }
        curApiKeyName={currentRow?.name}
        isLoading={updatingApiKey}
      />
      <RevokeDeprecateApiKeyConfirmationModal
        mode={apiKeyModal.mode}
        visible={
          apiKeyModal.visible &&
          ["revoke", "deprecate"].includes(apiKeyModal.mode)
        }
        onClose={() => setApiKeyModal({ visible: false, mode: "" })}
        onContinue={handleUpdateApiKey}
        apiKeyName={currentRow?.name}
        isLoading={updatingApiKey}
      />
      <CopyApiKeyModal
        visible={copyApiKeyModal.visible}
        onClose={() => setCopyApiKeyModal({ visible: false, apiKey: "" })}
        apiKey={copyApiKeyModal?.apiKey}
      />
      {!!rowOptionsVisible && (
        <div
          className="row-options-backdrop"
          onClick={() => setRowOptionsVisible(false)}
        >
          <div style={{ position: "relative" }}>
            <div
              className="row-options-foreground"
              style={{
                marginLeft: rowOptionsCords[0] - 80,
                marginTop: rowOptionsCords[1] + 10,
              }}
            >
              <button
                className="t6 nc-gray-900 menu-text-left"
                onClick={() => {
                  setApiKeyModal({
                    visible: true,
                    mode: "edit",
                  });
                }}
              >
                Rename
              </button>

              {currentRow?.status === "active" && (
                <button
                  className="t6 nc-gray-900 menu-text-left"
                  onClick={() => {
                    setApiKeyModal({ visible: true, mode: "deprecate" });
                  }}
                >
                  Deprecate
                </button>
              )}
              {currentRow?.status === "deprecated" && (
                <button
                  className="t6 nc-gray-900 menu-text-left"
                  onClick={() => {
                    setApiKeyModal({ visible: false, mode: "reactivate" });
                    handleUpdateApiKey(null, "reactivate");
                  }}
                >
                  Reactivate
                </button>
              )}
              {currentRow?.status !== "revoked" && (
                <button
                  className="t6 nc-gray-900 menu-text-left"
                  onClick={() => {
                    setApiKeyModal({ visible: true, mode: "revoke" });
                  }}
                >
                  Revoke
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DeveloperTab;
